import { useEffect, useRef, useState } from "react";
import * as d3 from "d3";
import * as topojson from "topojson-client";
import { Popper } from "@mui/material";

export default function BroadcastersUSMap({bubblesData, stateMapData, setMarketData}) {
    const svgRef = useRef();

    // Tooltip state
    const [tooltipHeader, setTooltipHeader] = useState("");
    const [tooltipContent, setTooltipContent] = useState("");
    const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
    const [tooltipOpen, setTooltipOpen] = useState(false);


    // Render the map and bubbles
    useEffect(() => {
        if (!stateMapData || !bubblesData || bubblesData.length === 0) return;
        
        d3.select(svgRef.current).selectAll("*").remove();
        const width = 975;
        const height = 610;

        const svg = d3.select(svgRef.current)
            .attr("viewBox", [0, 0, width, height])
            .attr("width", width)
            .attr("height", height)
            .attr("style", "max-width: 100%; height: auto; cursor: pointer;")
            .on("click", () => {
                resetZoom(); 
                setMarketData(null);
                stopPulsing();
            });

        const g = svg.append("g");

        const projection = d3.geoAlbersUsa().scale(1300).translate([487.5, 305]);
        const path = d3.geoPath(projection);

       

        const wiggleRoom = 50;
        const bounds = [[-wiggleRoom, -wiggleRoom], [wiggleRoom + width, wiggleRoom + height]];

        const zoom = d3.zoom()
            .scaleExtent([1, 8])
            .translateExtent(bounds)
            .on("zoom", zoomed);

        svg.call(zoom);

        // Draw the states
        const states = g.append("g")
            .attr("fill", "#444")
            .attr("cursor", "pointer")
            .attr("stroke", "white")
            .selectAll("path")
            .data(topojson.feature(stateMapData, stateMapData.objects.states).features)
            .join("path")
            .on("click", (event, d) => clickedElement(event, d, "state"))
            .on("mouseover", (event, d) => {
                setTooltipHeader(`${d.properties.name}`);
                setTooltipContent("");
                setTooltipPosition({ x: event.pageX + 15, y: event.pageY + 15 });
                setTooltipOpen(true);
            })
            .on("mousemove", (event) => {
                setTooltipPosition({ x: event.pageX + 15, y: event.pageY + 15 }); 
            })
            .on("mouseout", () => {
                setTooltipOpen(false); 
            })
            .attr("d", path);

            // bubblesData = bubblesData
            //console.log(bubblesData)

        // Add click event listener to bubbles
        g.selectAll(".bubble")
            .data(bubblesData)
            .join("circle")
            .attr("class", "bubble")
            .attr("cx", d => {
                const projectedCoordinates = projection([d.coordinates[1], d.coordinates[0]]);
                return projectedCoordinates ? projectedCoordinates[0] : null;
            })
            .attr("cy", d => {
                const projectedCoordinates = projection([d.coordinates[1], d.coordinates[0]]);
                return projectedCoordinates ? projectedCoordinates[1] : null;
            })
            .attr("r", d => d.size)
            .attr("fill", d => d.stations.some(station => station.live) ? "red" : "#1F3F66")
            .attr("stroke", "white")
            .attr("stroke-width", 1)
            .on("mouseover", (event, d) => {
                const stationDetails = d.stations
                    .map(station => `${station.call_sign} (${station.affiliation}): ${station.value} users \n`);
                setTooltipHeader(`${d.market}`);
                setTooltipContent(stationDetails.join("\n"));
                setTooltipPosition({ x: event.pageX + 15, y: event.pageY + 15 }); 
                setTooltipOpen(true);
            })
            .on("mousemove", (event) => {
                setTooltipPosition({ x: event.pageX + 15, y: event.pageY + 15 }); 
            })
            .on("mouseout", () => {
                setTooltipOpen(false);
            })
            .on("click", (event, d) => {
                clickedElement(event, d, "bubble");
            });

        // Store the pulse interval globally or in a Map keyed by station ID
        let pulseInterval = null;

        function startPulsing(d, projection) {
        const [x, y] = projection([d.coordinates[1], d.coordinates[0]]);

        stopPulsing(); // Stop any previous pulsing before starting new

        pulseInterval = d3.interval(() => {
            const pulse = g.append("circle")
            .attr("class", "pulse")
            .attr("cx", x)
            .attr("pointer-events", "none")
            .attr("cy", y)
            .attr("r", 0)
            .attr("fill", "none")
            .attr("stroke", "#FFFF")
            .attr("stroke-width", 2)
            .attr("opacity", 0.4);

            pulse.transition()
            .duration(1500)
            .attr("r", d.radioWaveSize)
            .attr("opacity", 0)
            .ease(d3.easeCubicOut)
            .remove();
        }, 1200);
        }

        function stopPulsing() {
        if (pulseInterval) {
            pulseInterval.stop();
            pulseInterval = null;
        }

        // Remove any lingering pulses from the SVG
        g.selectAll(".pulse").remove();
        }



        function resetZoom() {
            states.transition();
            svg.transition().duration(750).call(
                zoom.transform,
                d3.zoomIdentity,
                d3.zoomTransform(svg.node()).invert([width / 2, height / 2])
            );
        }

        let focusedState = null;
        let focusedBubble = null;

        function clickedElement(event, elementClicked, elementType) {
            if ((elementType === "state" && focusedState === elementClicked) || 
            (elementType === "bubble" && focusedBubble === elementClicked)) {
            focusedState = null;
            focusedBubble = null;
            stopPulsing();
            setMarketData(null);
            resetZoom();
            return;
            }

            if (elementType === "state") {
            stopPulsing();
            const [[x0, y0], [x1, y1]] = path.bounds(elementClicked);
            event.stopPropagation();
            states.transition();
            svg.transition().duration(750).call(
                zoom.transform,
                d3.zoomIdentity
                .translate(width / 2, height / 2)
                .scale(Math.min(8, 0.9 / Math.max((x1 - x0) / width, (y1 - y0) / height)))
                .translate(-(x0 + x1) / 2, -(y0 + y1) / 2),
                d3.pointer(event, svg.node())
            );
            focusedState = elementClicked;
            focusedBubble = null;
            setMarketData(null);
            } else if (elementType === "bubble") {
            const [x, y] = projection([elementClicked.coordinates[1], elementClicked.coordinates[0]]);
            event.stopPropagation();
            svg.transition().duration(750).call(
                zoom.transform,
                d3.zoomIdentity
                .translate(width / 2, height / 2)
                .scale(4)
                .translate(-x, -y),
                d3.pointer(event, svg.node())
            );
            setMarketData(elementClicked);
            focusedBubble = elementClicked;
            focusedState = null;
            stopPulsing();
            startPulsing(elementClicked, projection);
            }
        }

        function zoomed(event) {
            const { transform } = event;
            g.attr("transform", transform);
            g.attr("stroke-width", 1 / transform.k);
            g.selectAll(".bubble").attr("r", 10 / (transform.k)).attr("stroke-width", 1/ transform.k)
        }
    }, [stateMapData, setMarketData, bubblesData]);

    
    return (
        <>
            <svg ref={svgRef}></svg>
            <Popper
                open={tooltipOpen}
                style={{
                    position: "absolute",
                    top: tooltipPosition.y,
                    left: tooltipPosition.x,
                    pointerEvents: "none",
                    background: "white",
                    border: "1px solid black",
                    padding: "5px",
                    color: "black",
                    maxWidth: "300px",
                    boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
                }}
            >
                <strong>{tooltipHeader}</strong> 
                <br />
                {tooltipContent.split("\n").map((line, index) => (
                    <div key={index}>{line}</div>
                ))}
        </Popper>
        </>
    );
}